import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { openReplayStateLogger } from '../../../hooks/open-replay';

export interface CompareCompany {
  rcid: number;
  name: string;
}

/** Max user-added companies (excluding pinned current employer) */
const MAX_USER_PICKS = 3;

interface CompanyCompareState {
  /** Pinned current employer — always first in comparisons */
  currentEmployer: CompareCompany | null;
  /** User-selected companies to compare (up to MAX_USER_PICKS) */
  selectedCompanies: CompareCompany[];
  isDrawerOpen: boolean;
  /** Set the current employer (pinned, not removable) */
  setCurrentEmployer: (company: CompareCompany | null) => void;
  addCompany: (company: CompareCompany) => void;
  removeCompany: (rcid: number) => void;
  clearAll: () => void;
  isSelected: (rcid: number) => boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  /** All companies for comparison (current employer + selected) */
  getAllCompanies: () => CompareCompany[];
}

export const useCompanyCompareStore = create<CompanyCompareState>()(
  devtools(
    openReplayStateLogger(
      (set, get) => ({
        currentEmployer: null,
        selectedCompanies: [],
        isDrawerOpen: false,

        setCurrentEmployer: (company) => {
          set({ currentEmployer: company });
        },

        addCompany: (company) => {
          const { selectedCompanies, currentEmployer } = get();
          // Don't add if it's the current employer
          if (currentEmployer && company.rcid === currentEmployer.rcid) return;
          if (selectedCompanies.length >= MAX_USER_PICKS) return;
          if (selectedCompanies.some((c) => c.rcid === company.rcid)) return;
          set({ selectedCompanies: [...selectedCompanies, company] });
        },

        removeCompany: (rcid) => {
          const { selectedCompanies, currentEmployer } = get();
          // Don't allow removing pinned current employer
          if (currentEmployer && rcid === currentEmployer.rcid) return;
          set({
            selectedCompanies: selectedCompanies.filter((c) => c.rcid !== rcid),
          });
        },

        clearAll: () => set({ selectedCompanies: [], isDrawerOpen: false }),

        isSelected: (rcid) => {
          const { selectedCompanies, currentEmployer } = get();
          if (currentEmployer && currentEmployer.rcid === rcid) return true;
          return selectedCompanies.some((c) => c.rcid === rcid);
        },

        openDrawer: () => set({ isDrawerOpen: true }),
        closeDrawer: () => set({ isDrawerOpen: false }),

        getAllCompanies: () => {
          const { currentEmployer, selectedCompanies } = get();
          if (currentEmployer) {
            return [currentEmployer, ...selectedCompanies];
          }
          return selectedCompanies;
        },
      }),
      'company_compare'
    ),
    { name: 'CompanyCompare' }
  )
);
